// The candidate's interview screen. PUBLIC — no login, no session.
//
// This file deliberately imports nothing from the app's auth or ATS layers.
// A candidate is a logged-out stranger with a link; AutoTaskAuthContext,
// useATS() and ATSLayout do not exist for them, and importing any of it would
// crash the page for exactly the person it is built for.
//
// THE SHAPE OF THE PROBLEM
// Twelve answers of roughly a minute each is ~55 MB of video, recorded on a
// phone, on Indian mobile data, by someone who is nervous and gets one or two
// attempts. Almost every design decision here follows from that:
//
//   - Upload each answer as it finishes, never one file at the end. A single
//     large upload fails often and takes the whole interview with it.
//   - Upload the PREVIOUS answer while the candidate reads the NEXT question.
//     The upload is then free: it happens during time the candidate is using to
//     think, rather than making them watch a progress bar.
//   - Checkpoint after every answer, server-side. Closing the browser at Q7 and
//     reopening must resume at Q7 with the first six answers intact.
//   - A failed upload is retried and surfaced, never silently dropped.

import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import DeviceCheck from "./DeviceCheck";
import InterviewStage from "./InterviewStage";
import { startWavCapture } from "./wavRecorder";
import useQuestionSpeech from "./useQuestionSpeech";
import InterviewerGreeting from "./InterviewerGreeting";
import { Shell, Loading, Failure, Welcome, Complete } from "./InterviewScreens";

const API = `${process.env.REACT_APP_API_BASE_URL}`.replace(/\/+$/, "");

// Long enough to read the question as well as hear it — voice can be off,
// unsupported, or simply missed — and to take a breath before answering.
const PREP_SECONDS = 12;

// How short an answer has to be before it is treated as a slip rather than an
// answer. Well under anything real, comfortably over a double-tap.
const SHORT_ANSWER_MS = 5000;

// How long a held countdown sits before the candidate is nudged. Only reachable
// when they asked for more time, so it is a reminder rather than a prod.
const IDLE_NUDGE_MS = 45_000;

// Longest the countdown will wait for Maya's voice before starting anyway.
// Generous, because a slow clip on a poor connection is ordinary and cutting
// her off mid-question is worse than waiting; this is only here to stop a clip
// that never arrives from stranding the candidate on a silent screen.
const VOICE_WAIT_MAX_MS = 15_000;

// Picks a container the browser will actually produce. Chrome and Firefox give
// webm; Safari only recently supports it and prefers mp4. Asking for a type the
// browser cannot encode fails at record time, on the candidate's machine, where
// nobody can debug it.
function pickMimeType() {
  const candidates = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
    "video/mp4",
  ];
  for (const type of candidates) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }
  return "";
}

// Strips the codecs parameter before the blob goes on the wire.
//
// THIS IS NOT COSMETIC. MediaRecorder reports its type as
// `video/webm;codecs=vp9,opus`, and that string becomes the Content-Type of the
// multipart part verbatim. The comma is unquoted, which RFC 2045 does not allow
// in a parameter value, so busboy fails to parse the header and falls back to
// `text/plain` — the server never sees a video type at all. The upload then
// fails the storage allowlist and the candidate is told to check their
// connection, which is not the problem and cannot be acted on.
//
// Safari hits the same wall with `video/mp4;codecs=avc1.42E01E,mp4a.40.2`, so
// this is not a Chrome quirk to work around in one browser.
//
// The parameters are safe to drop: the container is self-describing, and every
// player that can read the file reads the codecs from the file itself.
function baseMimeType(type, fallback = "video/webm") {
  const base = String(type || "").split(";")[0].trim().toLowerCase();
  return base || fallback;
}

const AiInterview = () => {
  const token = new URLSearchParams(window.location.search).get("token");

  const [phase, setPhase] = useState("loading"); // loading|error|ready|device|interview|done
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [session, setSession] = useState(null);
  const [questions, setQuestions] = useState([]);
  // Which answer triggers deepening. Server-supplied, because it moves with how
  // many questions the hiring team authored for this job.
  const [deepenAfterIndex, setDeepenAfterIndex] = useState(null);
  // Who is conducting the interview, and what she says before the first
  // question. Absent on a resume — see the note where it is set.
  const [interviewer, setInterviewer] = useState(null);
  const [index, setIndex] = useState(0);
  const [recording, setRecording] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadFailed, setUploadFailed] = useState(null);
  const [starting, setStarting] = useState(false);

  // ── Guiding the candidate ─────────────────────────────────────────────────
  //
  // Three pieces of state, all there because candidates were getting lost in a
  // screen that waited silently for them to notice a button.
  //
  // prepLeft   seconds until recording starts on its own. The commonest failure
  //            was simply forgetting to press record — the question was read
  //            aloud, nothing happened, and they sat waiting for the system
  //            while the system waited for them. A countdown removes the button
  //            from the critical path without taking away control: it can be
  //            started early or held off.
  // holding    true when they asked for more time, which stops the countdown
  //            rather than resetting it.
  // pendingShort  a finished recording too short to be plausible, held back
  //            from upload until they confirm. See stopRecording.
  const [prepLeft, setPrepLeft] = useState(null);
  const [holding, setHolding] = useState(false);
  const [pendingShort, setPendingShort] = useState(null);
  // Nudge shown when a question has sat unanswered with no recording started.
  const [idle, setIdle] = useState(false);
  // Set when the voice has been "about to speak" for too long, which releases
  // the countdown so a failed clip cannot strand somebody on a silent screen.
  const [voiceWaitExpired, setVoiceWaitExpired] = useState(false);

  // Deepening is fetched once per interview, and the widened trigger below
  // (>= rather than ===) would otherwise re-fetch on every later answer.
  //
  // Two refs rather than one boolean, because "we got an answer" and "we tried"
  // are different facts. Latching on the attempt would mean a single dropped
  // request costs the interview its two follow-ups permanently — which is the
  // exact failure the widened trigger exists to prevent, reintroduced one line
  // higher up. So a failure is allowed to retry on the next answer, capped,
  // because each attempt is a model call and a genuinely broken endpoint must
  // not be retried once per remaining question.
  const deepenDoneRef = useRef(false);
  const deepenTriesRef = useRef(0);
  const DEEPEN_MAX_TRIES = 2;

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const questionShownAtRef = useRef(null);
  const recordStartedAtRef = useRef(null);
  const timerRef = useRef(null);
  // Resolves to the parallel WAV capture handle. Held as the promise rather
  // than the handle because starting a worklet is async and recording must not
  // wait on it — see beginRecording.
  const wavRef = useRef(null);

  // The interviewer's voice. Owned here rather than in InterviewStage because
  // recording has to be able to silence it, and InterviewStage is presentation
  // only — it does not know when a recorder starts.
  const speech = useQuestionSpeech({ token });

  const current = questions[index] || null;

  // ── Join ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!token) {
      setError("This link is missing its access token.");
      setPhase("error");
      return;
    }
    (async () => {
      try {
        const res = await axios.get(
          `${API}/api/hiring/public/interview/session`,
          { params: { token } },
        );
        setSession(res.data.data);
        setPhase("ready");
      } catch (err) {
        const body = err.response?.data;
        setErrorCode(body?.code || null);
        setError(body?.statusMessage || "We could not open this interview.");
        setPhase("error");
      }
    })();
  }, [token]);

  // ── Camera ────────────────────────────────────────────────────────────────
  const openCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        // ── Sized for what these recordings are actually watched at ───────
        //
        // 480x360, not 640x480. The review console shows an answer as a 176px
        // thumbnail and, when opened, in a dialog capped at 76vh — the extra
        // pixels were never visible at either size, and they were being paid
        // for by the candidate's upload.
        //
        // 24fps rather than the 30 the camera defaults to, and not the 15 that
        // would save more. These recordings are scored partly on confidence and
        // manner, and 15fps judders on a nod or a gesture — which makes a
        // nervous candidate look worse than they are. That is a scoring risk,
        // and it is not worth the megabyte it saves.
        //
        // `ideal` throughout: a camera that cannot do exactly this gives its
        // nearest match instead of failing, and a refused getUserMedia here
        // ends the interview before it starts.
        video: {
          width: { ideal: 480 },
          height: { ideal: 360 },
          frameRate: { ideal: 24, max: 30 },
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          // Mono. Speech from one person at one microphone carries nothing in a
          // second channel, and a stereo mic would otherwise double the audio
          // in the recording for no gain.
          //
          // NOTE: there is no sampleRate here on purpose. It used to ask for
          // 44100 with a comment claiming that kept files small. It did
          // neither — Opus resamples to 48k internally whatever it is handed,
          // and the transcription stream is a separate 16kHz mono WAV built in
          // wavRecorder.js, which is the one that actually had to be right.
          channelCount: { ideal: 1 },
        },
      });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      return true;
    } catch (err) {
      setError(
        "We could not access your camera and microphone. Please allow access and reload.",
      );
      setPhase("error");
      return false;
    }
  }, []);

  // Re-attach the stream whenever the phase changes.
  //
  // Each phase renders its own <video> element — the device check has one, the
  // interview screen has another. Setting srcObject once inside openCamera bound
  // it to whichever element existed at that moment, and moving to the next phase
  // mounted a fresh node with nothing attached: a black rectangle, a candidate
  // who cannot see themselves, and no way to tell whether the camera works.
  //
  // Keyed on phase rather than done once, because the element identity changes
  // with the phase, not with the stream.
  useEffect(() => {
    if (phase !== "interview") return;
    if (videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      // Autoplay can be refused when the element remounts; play() is a no-op if
      // it is already running.
      videoRef.current.play?.().catch(() => {});
    }
  }, [phase]);

  useEffect(() => {
    return () => {
      // Release the camera light when leaving, whatever the reason.
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // ── Integrity beacons ─────────────────────────────────────────────────────
  //
  // Recorded as evidence for the human reviewer and nothing else — they do not
  // affect the score, end the session, or trigger anything automatic. Every one
  // of these has an innocent explanation (a notification, a dropped call, a
  // parent walking in), so a system that quietly penalised people for them would
  // be unfair and impossible to defend when challenged.
  //
  // Fire-and-forget: a failed beacon must never interrupt an interview.
  const beacon = useCallback(
    (type) => {
      if (!token) return;
      axios
        .post(`${API}/api/hiring/public/interview/integrity`, {
          token,
          type,
          question_index: questions[index]?.index ?? null,
        })
        .catch(() => {});
    },
    [token, questions, index],
  );

  useEffect(() => {
    if (phase !== "interview") return undefined;
    const onVisibility = () => {
      if (document.hidden) beacon("TAB_HIDDEN");
    };
    const onBlur = () => beacon("WINDOW_BLUR");
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", onBlur);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", onBlur);
    };
  }, [phase, beacon]);

  // ── Device check ──────────────────────────────────────────────────────────
  const openDeviceCheck = async () => {
    setStarting(true);
    setError(null);
    const cameraOk = await openCamera();
    setStarting(false);
    if (cameraOk) setPhase("device");
  };

  const completeDeviceCheck = async (result) => {
    try {
      await axios.post(`${API}/api/hiring/public/interview/device-check`, {
        token,
        ...result,
      });
    } catch {
      // Recording the check is useful context for the reviewer, not a gate.
      // Failing to store it must not stop the interview.
    }
    await startInterview();
  };

  // ── Start / resume ────────────────────────────────────────────────────────
  const startInterview = async () => {
    setStarting(true);
    setError(null);
    if (!streamRef.current) {
      const cameraOk = await openCamera();
      if (!cameraOk) {
        setStarting(false);
        return;
      }
    }
    try {
      const res = await axios.post(
        `${API}/api/hiring/public/interview/start`,
        { token },
      );
      const data = res.data.data;
      setQuestions(data.questions || []);
      setDeepenAfterIndex(data.deepen_after_index ?? null);
      // Only a fresh start carries a greeting. Someone resuming at question
      // seven does not need introducing to the interview again, and hearing the
      // welcome a second time reads as having lost their progress.
      setInterviewer(data.interviewer || null);
      // Resuming lands on the first UNANSWERED question, not back at the start.
      setIndex(data.resumed ? data.answered || 0 : 0);
      setPhase(data.interviewer?.greeting ? "greeting" : "interview");
      questionShownAtRef.current = Date.now();
    } catch (err) {
      const body = err.response?.data;
      setErrorCode(body?.code || null);
      setError(body?.statusMessage || "We could not start your interview.");
      setPhase("error");
    } finally {
      setStarting(false);
    }
  };

  // ── The interviewer reads the question ────────────────────────────────────
  //
  // Keyed on the question index, not on `current`, so an unrelated re-render
  // does not restart the sentence halfway through.
  //
  // Guarded on `recording` for the reason that matters: if the voice is still
  // going when the microphone opens, it lands in the recording and is
  // transcribed as if the candidate said it — the interviewer's own words then
  // reach the scorer as the candidate's answer.
  // ── Keyed on the question's IDENTITY, not its position ───────────────────
  //
  // This guarded on `current.index`, and that is precisely how Maya ended up
  // silent on the deepening questions. Deepening is spliced in and everything
  // after it renumbered, so position 12 stopped being the logistics question
  // and became a follow-up — with the same index. The guard saw "already read
  // index 12", returned, and the new question was never spoken at all.
  //
  // `qid` is fixed for the life of a question, so a position that changes
  // meaning now reads as what it is: a different question, which has not been
  // read aloud yet.
  const spokenForRef = useRef(null);
  useEffect(() => {
    if (phase !== "interview" || recording || !current?.question) return;
    const key = current.qid || `i${current.index}`;
    if (spokenForRef.current === key) return;
    spokenForRef.current = key;
    speech.speak(current.question, current.index, current.qid);

    // Warm the next question's audio while this one is being answered, so the
    // gap between questions is a network round trip rather than a synthesis.
    const next = questions[index + 1];
    if (next) speech.prefetch(next.index, next.qid);
  }, [
    phase,
    recording,
    current?.index,
    current?.qid,
    current?.question,
    speech,
    questions,
    index,
  ]);

  // ── The countdown to recording ────────────────────────────────────────────
  //
  // Armed once Maya has finished asking, never while she is still speaking —
  // starting the microphone under her voice is the bleed the whole design
  // avoids, and a countdown running behind the question is also just confusing.
  //
  // PREP_SECONDS has to cover reading the question as well as thinking about
  // it, because voice can be off, unsupported, or simply not heard. Twelve
  // seconds is comfortable for both, and anybody who wants longer says so.
  //
  // Reset per question on `current.index`, so arriving at a new question always
  // restarts the clock rather than inheriting the last one's remainder.
  useEffect(() => {
    if (phase !== "interview") return undefined;
    if (recording || uploading || pendingShort || holding) return undefined;
    // Waiting on Maya, but not indefinitely.
    //
    // A voice clip that never resolves — a failed synthesis, a blob that will
    // not load, a provider having a bad minute — would otherwise hold the
    // countdown forever, and the candidate sits in front of a question with
    // nothing telling them what to do and nothing recording. Silence from the
    // interviewer must degrade into a readable question, not into a dead end.
    if ((speech?.speaking || speech?.loading) && !voiceWaitExpired) return undefined;
    if (prepLeft === null) {
      setPrepLeft(PREP_SECONDS);
      return undefined;
    }
    if (prepLeft <= 0) return undefined;

    const t = setTimeout(() => setPrepLeft((s) => (s === null ? null : s - 1)), 1000);
    return () => clearTimeout(t);
  }, [
    // voiceWaitExpired is load-bearing, not decorative: it is the thing that
    // releases the wait above, and without it in this list the effect would not
    // re-run when it flips — the countdown would stay parked exactly in the
    // case the escape hatch exists to rescue.
    voiceWaitExpired,
    phase,
    recording,
    uploading,
    pendingShort,
    holding,
    prepLeft,
    speech?.speaking,
    speech?.loading,
  ]);

  // Fires the recorder when the countdown lands. Separated from the tick above
  // so the timer is not responsible for a side effect on the media stack.
  //
  // LATCHED PER QUESTION, and it has to be. `prepLeft` stays at 0 once it has
  // landed, so without this the effect re-fires on the next unrelated render —
  // and there are two places where that is reachable and harmful:
  //
  //   the last question, where finishing does NOT advance the index, so the
  //   window between the answer uploading and the phase becoming "done" would
  //   open the microphone again on a finished interview;
  //
  //   after answering again, where the same index comes back around.
  //
  // The latch is cleared deliberately in redoAnswer, which is the one case
  // where re-arming on the same question is the point.
  const autoStartedForRef = useRef(null);
  useEffect(() => {
    if (phase !== "interview" || recording || uploading || pendingShort) return;
    if (prepLeft !== 0) return;
    if (autoStartedForRef.current === current?.index) return;
    autoStartedForRef.current = current?.index ?? null;
    beginRecording();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prepLeft, phase, recording, uploading, pendingShort, current?.index]);

  // A fresh question clears the previous one's countdown and any held state.
  useEffect(() => {
    setPrepLeft(null);
    setHolding(false);
    setIdle(false);
    setVoiceWaitExpired(false);
  }, [current?.index]);

  // The deadline on waiting for the voice. Generous — a slow clip is normal on
  // a poor connection and cutting Maya off mid-sentence to start a countdown
  // would be worse than waiting. This only has to catch the case where the
  // clip never arrives at all.
  useEffect(() => {
    if (phase !== "interview" || recording || !current?.question) return undefined;
    if (!speech?.speaking && !speech?.loading) return undefined;
    const t = setTimeout(() => setVoiceWaitExpired(true), VOICE_WAIT_MAX_MS);
    return () => clearTimeout(t);
  }, [phase, recording, current?.question, speech?.speaking, speech?.loading]);

  // ── The nudge ─────────────────────────────────────────────────────────────
  //
  // The worst case this catches is silent and total: a candidate who never
  // presses record, answers the question out loud to a camera that is not
  // recording, and finds out nothing was captured only if somebody reviews it.
  // Only reachable when they have held the countdown off — otherwise recording
  // has already started on its own.
  useEffect(() => {
    if (phase !== "interview" || recording || !holding) {
      return undefined;
    }
    const t = setTimeout(() => setIdle(true), IDLE_NUDGE_MS);
    return () => clearTimeout(t);
  }, [phase, recording, holding, current?.index]);

  // ── Recording ─────────────────────────────────────────────────────────────
  const beginRecording = () => {
    if (!streamRef.current) return;
    // Unconditional, and before anything else. See the note on the effect above:
    // an utterance still playing when the mic opens becomes part of the answer.
    speech.cancel();
    chunksRef.current = [];
    const mimeType = pickMimeType();
    // ── Bitrate, because the default is wrong for this ────────────────────
    //
    // Left unset, Chrome encodes at roughly 2.5 Mbit/s. At the 640x480 this
    // stream is constrained to, that produced 23MB for 74 seconds of a person
    // sitting still and talking — around four times what the picture needs.
    //
    // WHO PAYS FOR THAT, in order of how much it hurts:
    //
    //   The candidate, who UPLOADS it. Sixteen answers at that rate is most of
    //   a third of a gigabyte over whatever connection they have, between
    //   questions, on a phone. Every upload that failed and left a question
    //   with no recording was this bill being presented.
    //
    //   The reviewer, who waits for it to arrive before it will play.
    //
    //   The storage account, which keeps all of it forever.
    //
    // 500kbps at the 480x360/24fps this stream is constrained to works out
    // around 0.12 bits per pixel per frame, which is a comfortable margin for
    // one face against one wall — the case inter-frame compression is best at.
    // It holds up when the candidate gestures, which is the moment a tighter
    // cap goes soft and also the moment somebody is watching closely.
    //
    // Raising the frame rate does NOT cost bytes at a fixed cap; it spends the
    // same bits across more frames. 500k rather than 350k is what buys back
    // the per-frame quality those extra frames would otherwise dilute.
    //
    // Audio gets 48kbps Opus, which is transparent for mono speech. It is only
    // ever heard by a human spot-checking the recording — scoring reads the
    // separate 16kHz WAV from wavRecorder.js, so there is nothing riding on
    // this stream's fidelity.
    //
    // Net: ~19MB per minute originally, ~4.1MB now.
    //
    // Options are advisory: a browser that does not honour them ignores them
    // rather than throwing, so there is nothing to feature-detect.
    const recorder = new MediaRecorder(streamRef.current, {
      ...(mimeType ? { mimeType } : {}),
      videoBitsPerSecond: 500_000,
      audioBitsPerSecond: 48_000,
    });
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.start();
    recorderRef.current = recorder;
    recordStartedAtRef.current = Date.now();
    setRecording(true);

    // Second capture of the same microphone, as WAV, because the transcription
    // API will not accept the webm above. Deliberately NOT awaited: the video is
    // already recording, and holding the candidate on a spinner while an
    // AudioWorklet loads would clip the first second of every answer.
    wavRef.current = startWavCapture(streamRef.current).catch((err) => {
      console.warn("[interview] wav capture failed to start:", err?.message);
      return null;
    });

    const limit = current?.max_duration_sec || 120;
    setSecondsLeft(limit);
    timerRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(timerRef.current);
          // Auto-stop at the limit rather than letting an answer run forever.
          // The candidate is told the limit up front, so this is a deadline they
          // knew about, not a surprise cut-off.
          stopRecording();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  const stopRecording = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === "inactive") return;

    recorder.onstop = async () => {
      setRecording(false);
      const blob = new Blob(chunksRef.current, {
        type: baseMimeType(chunksRef.current[0]?.type),
      });

      // Null whenever capture was unavailable or produced nothing. The answer
      // still uploads; the server records a transcription error against it and
      // the reviewer watches the video instead.
      let audioBlob = null;
      // Null when capture was unavailable, which is a different thing from
      // "measured and found silent" — see the check below.
      let heardSpeech = null;
      try {
        const capture = await wavRef.current;
        const result = capture ? await capture.stop() : null;
        audioBlob = result?.blob || null;
        if (result && typeof result.hasSpeech === "boolean") {
          heardSpeech = result.hasSpeech;
        }
      } catch (err) {
        console.warn("[interview] wav capture failed:", err?.message);
      } finally {
        wavRef.current = null;
      }
      const answeredIndex = current?.index;
      const durationMs = Date.now() - (recordStartedAtRef.current || Date.now());
      const latencyMs =
        (recordStartedAtRef.current || 0) - (questionShownAtRef.current || 0);

      const payload = {
        blob,
        audioBlob,
        answeredIndex,
        durationMs,
        latencyMs,
        heardSpeech,
      };

      // ── Catch the accidental stop BEFORE anything is committed ──────────
      //
      // The most expensive mistake a candidate can make here used to cost them
      // a whole question: press record, press stop again by reflex, and the
      // interview advanced, uploaded two seconds of nothing, and told them
      // they could not come back to it. That silence then became an invented
      // answer they were scored on.
      //
      // Held rather than uploaded, so choosing to answer again needs no server
      // involvement at all — nothing was sent, so there is no stored attempt to
      // replace and the index-idempotency guard is never in play.
      //
      // Two ways to land here, and the second is the one that matters more.
      //
      //   TOO SHORT   the double-tap. Caught on duration alone, because a
      //               two-second recording is a slip whatever is in it.
      //
      //   NO SPEECH   a recording of any length containing no voice. A muted
      //               microphone, a headset on the wrong input, a browser that
      //               granted the camera and not the mic. The candidate talks
      //               for ninety seconds, sees the timer running, and has no
      //               way of knowing nothing was captured — and the server
      //               would accept it, store it, and score them as absent.
      //
      // `heardSpeech === false` specifically, not falsy: null means capture was
      // unavailable and nothing was measured, and an unmeasured answer must go
      // through untouched rather than be challenged on a guess.
      //
      // Everything else still advances instantly, which is what keeps a long
      // interview from feeling like a queue.
      if (durationMs < SHORT_ANSWER_MS || heardSpeech === false) {
        setPendingShort(payload);
        return;
      }

      await commitAnswer(payload);
    };
    recorder.stop();
  };

  // Advance and upload. Split out of stopRecording so the short-answer confirm
  // can call it later without re-running the capture teardown.
  const commitAnswer = async ({ blob, audioBlob, answeredIndex, durationMs, latencyMs }) => {
    setPendingShort(null);
    // Disarm before the upload. On the last question nothing advances the
    // index, so leaving the countdown at 0 would leave a landed countdown
    // sitting behind a finished interview.
    setPrepLeft(null);

    // ADVANCE FIRST, UPLOAD AFTER.
    // The candidate moves to the next question immediately and reads it while
    // the previous answer uploads behind them. Making them wait on a progress
    // bar between every question adds minutes of dead time to a 30-minute
    // interview and makes a slow connection feel like a broken app.
    const isLast = index >= questions.length - 1;
    if (!isLast) {
      setIndex((i) => i + 1);
      questionShownAtRef.current = Date.now();
    }

    await uploadAnswer({
      blob,
      audioBlob,
      answeredIndex,
      durationMs,
      latencyMs,
      isLast,
    });
  };

  // Discard the held recording and let them answer again. Nothing was uploaded,
  // so this is genuinely a fresh attempt rather than an edit of a stored one.
  const redoAnswer = () => {
    setPendingShort(null);
    chunksRef.current = [];
    questionShownAtRef.current = Date.now();
    // Re-arm rather than fire. Clearing the latch and resetting the countdown
    // to null lets it run again from the top — dropping them straight back into
    // a live microphone the instant they ask to try again is exactly the abrupt
    // start this whole flow exists to remove.
    autoStartedForRef.current = null;
    setPrepLeft(null);
    setHolding(false);
  };

  // ── Upload ────────────────────────────────────────────────────────────────
  const uploadAnswer = async ({
    blob,
    audioBlob,
    answeredIndex,
    durationMs,
    latencyMs,
    isLast,
  }) => {
    setUploading(true);
    setUploadFailed(null);
    const form = new FormData();
    form.append("token", token);
    form.append("question_index", String(answeredIndex));
    form.append("duration_ms", String(durationMs));
    form.append("duration_sec", String(Math.round(durationMs / 1000)));
    form.append("latency_ms", String(Math.max(0, latencyMs)));
    form.append("recording", blob, `q${answeredIndex}.webm`);
    // Optional second file. Its absence is not an upload failure — the video is
    // the record, and the transcript is recoverable from it by a human.
    if (audioBlob) form.append("audio", audioBlob, `q${answeredIndex}.wav`);

    // An empty recording is worth catching here rather than sending. It happens
    // when the time limit is very short, or the recorder is stopped before it
    // produces a chunk — and it reaches the server as a file upload that fails
    // for a reason that looks nothing like the actual cause.
    if (!blob || blob.size === 0) {
      setUploadFailed({
        blob, audioBlob, answeredIndex, durationMs, latencyMs, isLast,
        message: "That recording came through empty. Please record the answer again.",
      });
      setUploading(false);
      return;
    }

    try {
      await axios.post(`${API}/api/hiring/public/interview/answer`, form);

      const mergedList = await maybeFetchDeepen(answeredIndex);

      // Recomputed against the list we now hold. `isLast` was decided before
      // the fetch, from a list that did not yet contain the follow-ups — and on
      // a job with no hard gates the deepen marker IS the last question, so
      // trusting it would submit the interview the moment the follow-ups
      // arrived and never ask them.
      // Falls back to `isLast` unless the merged list yields a real number.
      // Without that guard a question arriving without an index makes this
      // `answeredIndex >= undefined`, which is false forever: the candidate
      // answers the last question and the interview never submits. Hanging is
      // a worse failure than finishing two questions early, so the fallback is
      // the old value rather than `false`.
      const lastIndex = Number(mergedList?.[mergedList.length - 1]?.index);
      const reallyLast = Number.isFinite(lastIndex)
        ? answeredIndex >= lastIndex
        : isLast;

      if (reallyLast) await finishInterview();
    } catch (err) {
      // Kept in state with the blob so the candidate can retry rather than lose
      // the answer. Silent failure here would mean an interview that looks
      // complete but is missing an answer nobody knows about.
      //
      // The server's own message is carried through: "we could not save your
      // recording" tells the candidate nothing they can act on, and tells
      // whoever debugs this later even less.
      setUploadFailed({
        blob, audioBlob, answeredIndex, durationMs, latencyMs, isLast,
        message: err.response?.data?.statusMessage || err.message || null,
      });
    } finally {
      setUploading(false);
    }
  };

  // ── Deepening ─────────────────────────────────────────────────────────────
  //
  // Generated from what the candidate has actually said, so it is fetched once
  // they are past the pre-deepen block. The trigger index comes from the
  // server, not a hardcoded question number — the interview's length depends on
  // how many screening questions the hiring team authored, so "after question
  // 8" pointed somewhere different on every job.
  //
  // `>=` AND A REF, NOT `===`. Strict equality hung the whole block on one
  // specific answer reaching this line; miss that moment — a failed upload, a
  // resumed session, a skip — and the two follow-ups vanished with nothing
  // anywhere saying why. The refs are what stop a widened trigger re-firing on
  // every answer after, and they are two rather than one so that a dropped
  // REQUEST can retry while a real answer cannot be asked for twice.
  //
  // Called from both the upload path and the skip path. Skipping the marker
  // question is otherwise a way to lose the follow-ups entirely — and worse, to
  // have them arrive after a logistics answer is already stored, which the
  // server now refuses because re-numbering under a stored answer repoints it
  // at a question the candidate never heard.
  //
  // Returns the merged question list when it has one in hand, else null.
  const maybeFetchDeepen = async (answeredIndex) => {
    if (
      !deepenAfterIndex ||
      answeredIndex < deepenAfterIndex ||
      deepenDoneRef.current ||
      deepenTriesRef.current >= DEEPEN_MAX_TRIES
    ) {
      return null;
    }
    deepenTriesRef.current += 1;
    try {
      const res = await axios.post(
        `${API}/api/hiring/public/interview/deepen`,
        { token },
      );
      const payload = res.data.data || {};
      const returned = payload.questions || [];
      // The server answered. An empty list is still an answer — it means there
      // was nothing to probe — so this must not be retried.
      deepenDoneRef.current = true;
      if (!returned.length) return null;

      // The server splices the follow-ups in before the logistics block and
      // re-numbers everything after them, so it returns the whole list. Only
      // that form gives us the list in hand; the fallback stays a functional
      // update, because reading `questions` from this closure would be reading
      // a render-old array.
      // Stop whatever is playing before the list changes underneath it.
      //
      // The candidate has already advanced onto the position the follow-ups are
      // about to occupy, and Maya has started reading whatever used to be
      // there. Without this she audibly finishes the wrong question and only
      // then starts the right one — which is what a candidate reported as
      // "she's reading a different question to the one on screen".
      speech.cancel();

      setQuestions(
        payload.replaces_list
          ? returned
          : (qs) => [...qs, ...returned].sort((a, b) => a.index - b.index),
      );
      return payload.replaces_list ? returned : null;
    } catch (err) {
      // Non-fatal. Losing the tailored follow-ups must not end an interview
      // eight answers in. Logged rather than swallowed: this failing is how an
      // interview ends up two questions short, and a bare catch left no trace.
      console.warn("deepen fetch failed:", err.message);
      return null;
    }
  };

  // ── Skip ──────────────────────────────────────────────────────────────────
  //
  // A candidate who does not know an answer was previously left with one way
  // through: press record, sit in silence for three seconds, press stop. That
  // is what produced the silent clips the transcriber invented answers from, so
  // this button is not a convenience — it is the honest form of something they
  // were already doing, and the server now refuses to invent over it either way.
  //
  // Advances first, exactly as a recorded answer does, so a skip feels like the
  // same act and a slow network never strands somebody on a question they have
  // already decided to pass.
  //
  // Not offered on logistics questions: the server rejects that (it would turn
  // a hard gate into a free pass) and the UI must not present a button that
  // comes back with an error.
  const skipQuestion = async () => {
    if (recording || uploading) return;
    const answeredIndex = current?.index;
    const latencyMs = Date.now() - (questionShownAtRef.current || Date.now());
    const isLast = index >= questions.length - 1;

    if (!isLast) {
      setIndex((i) => i + 1);
      questionShownAtRef.current = Date.now();
    }

    setUploading(true);
    setUploadFailed(null);
    try {
      const form = new FormData();
      form.append("token", token);
      form.append("question_index", String(answeredIndex));
      form.append("skipped", "true");
      form.append("latency_ms", String(Math.max(0, latencyMs)));
      await axios.post(`${API}/api/hiring/public/interview/answer`, form);

      // Same as a recorded answer: skipping the marker question must still
      // fetch the follow-ups, or passing on one question silently costs two
      // more — and leaves the fetch to fire later, after a gate has been
      // answered, which the server then has to refuse.
      const mergedList = await maybeFetchDeepen(answeredIndex);
      const lastIndex = Number(mergedList?.[mergedList.length - 1]?.index);
      const reallyLast = Number.isFinite(lastIndex)
        ? answeredIndex >= lastIndex
        : isLast;

      if (reallyLast) await finishInterview();
    } catch (err) {
      // No blob to keep, so there is nothing to retry with — the candidate is
      // told and left on the next question rather than blocked.
      setUploadFailed({
        message:
          err.response?.data?.statusMessage ||
          "We could not record that skip. Please carry on.",
      });
    } finally {
      setUploading(false);
    }
  };

  const retryUpload = () => {
    // Only a failure that still holds its recording can be retried. A failed
    // skip has no blob, and re-entering uploadAnswer with nothing to send would
    // trip the empty-recording guard and tell the candidate their recording
    // came through empty — for a question they chose not to record.
    if (uploadFailed?.blob) uploadAnswer(uploadFailed);
  };

  const finishInterview = async () => {
    try {
      const res = await axios.post(`${API}/api/hiring/public/interview/submit`, {
        token,
      });
      // Maya's sign-off. Merged rather than replaced so her name survives even
      // if the submit response is missing the farewell for any reason.
      const said = res.data?.data?.interviewer;
      if (said) setInterviewer((prev) => ({ ...(prev || {}), ...said }));
    } catch {
      // Submitting is idempotent server-side; a failure here does not lose the
      // answers, which are already stored.
    }
    speech.cancel();
    if (wavRef.current) {
      // Abandoning a capture leaves an AudioContext and a live worklet node
      // attached to a track we are about to stop.
      wavRef.current.then((c) => c?.cancel?.()).catch(() => {});
      wavRef.current = null;
    }
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    setPhase("done");
  };

  // ── Render ────────────────────────────────────────────────────────────────
  //
  // Every screen the candidate sees lives in InterviewScreens, sharing one dark
  // surface and one type scale. They used to be ad-hoc JSX here in three
  // different visual languages, two of them light-themed against a dark
  // interview — crossing from the welcome into the questions looked like
  // crossing between two products.

  if (phase === "loading") return <Loading />;

  if (phase === "error") return <Failure message={error} code={errorCode} />;

  if (phase === "done") return <Complete interviewer={interviewer} speech={speech} />;

  if (phase === "ready") {
    return (
      <Welcome
        session={session}
        starting={starting}
        onContinue={openDeviceCheck}
      />
    );
  }

  if (phase === "device") {
    return (
      <Shell>
        <DeviceCheck
          stream={streamRef.current}
          onPass={completeDeviceCheck}
          onRetry={openCamera}
        />
      </Shell>
    );
  }

  if (phase === "greeting") {
    return (
      <InterviewerGreeting
        interviewer={interviewer}
        speech={speech}
        onBegin={() => {
          // Stop the welcome before the first question is spoken over it, and
          // before any chance of it bleeding into a recording.
          speech.cancel();
          setPhase("interview");
          questionShownAtRef.current = Date.now();
        }}
      />
    );
  }

  // phase === "interview" — presentation lives in InterviewStage; all state and
  // side effects stay here so the view has none.
  return (
    <InterviewStage
      session={session}
      questions={questions}
      current={current}
      index={index}
      recording={recording}
      secondsLeft={secondsLeft}
      uploading={uploading}
      uploadFailed={uploadFailed}
      videoRef={videoRef}
      stream={streamRef.current}
      onStart={() => {
        setPrepLeft(null);
        setHolding(false);
        beginRecording();
      }}
      onStop={stopRecording}
      onRetryUpload={retryUpload}
      onSkip={skipQuestion}
      // The guided flow. See the countdown effects above for why each exists.
      prepLeft={prepLeft}
      holding={holding}
      idle={idle}
      onHold={() => {
        setHolding(true);
        setPrepLeft(null);
      }}
      onResumeCountdown={() => {
        setHolding(false);
        setIdle(false);
        setPrepLeft(PREP_SECONDS);
      }}
      pendingShort={pendingShort}
      onKeepShort={() => commitAnswer(pendingShort)}
      onRedo={redoAnswer}
      // Logistics questions are the hard gates. The server refuses to skip one,
      // because an unanswered gate is treated as "do not fail" and skipping
      // would turn it into a free pass — so the button must not be offered.
      canSkip={current?.category !== "LOGISTICS"}
      speech={speech}
      interviewerName={interviewer?.name}
    />
  );
};

export default AiInterview;
